import {
  Box,
  Text,
  IconButton,
  FormControl,
  InputGroup,
  Input,
  FormErrorMessage,
  Flex,
  VisuallyHidden,
  InputLeftElement,
  Icon,
} from '@everlywell/ui-kit';
import { Tag, Trash, Plus } from '@phosphor-icons/react';
import { AxiosError } from 'axios';
import { PROMOCODE_PARAM } from 'components/CheckoutML/hooks/useUpdateOrderWithUrlParams/useUpdateOrderWithUrlParams';
import { PromoFormValues } from 'components/PromoCode/PromoCode';
import React, { MouseEventHandler } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { STORE_ROOT } from 'utils/constants/urls';
import { AxiosErrorResponse } from 'utils/types';

type PromoCodeSubmissionProps = {
  promoMessage: string;
  promotion: string | null;
  showLoadingState?: boolean;
  isLoading?: boolean;
  applyPromoMutation: any;
  removePromoCodeHandler: MouseEventHandler<HTMLButtonElement>;
  handlePromoCodeSubmit: (event: React.FormEvent) => void;
  formState: UseFormMethods<PromoFormValues>['formState'];
  control: UseFormMethods<PromoFormValues>['control'];
  promoCodeInputValue: string;
  setPromoCodeInputValue: (value: string) => void;
  wasAdvertisedPromoSubmitted: boolean;
  advertisedPromosElement: React.ReactNode | null;
  shouldShowInputIcon: boolean;
  /**
   * Shows lightGrey border instead of uiGrey
   */
  isVariant?: boolean;
};

const getPromoErrorMessage = (
  formState: UseFormMethods<PromoFormValues>['formState'],
  applyPromoMutation: any,
): string | React.ReactNode => {
  if (formState.isDirty && formState.errors.promoCode?.message) {
    return formState.errors.promoCode?.message;
  }

  const errors = (applyPromoMutation?.error as AxiosErrorResponse)?.response
    ?.data?.errors;
  if ((errors?.[0] as unknown as AxiosError)?.message) {
    return (errors?.[0] as unknown as AxiosError).message;
  }

  if (errors?.[0]) {
    const message = errors[0];

    if (message.includes('login')) {
      const redirectUrl = new URL(`${STORE_ROOT}/login`);

      redirectUrl.searchParams.set('my_results_redirect', window.location.href);
      redirectUrl.searchParams.set(
        PROMOCODE_PARAM,
        applyPromoMutation.variables.code,
      );

      return (
        <div
          dangerouslySetInnerHTML={{
            __html: message.replace(
              'login',
              `<a href="${redirectUrl.toString()}">login</a>`,
            ),
          }}
        />
      );
    }

    return message;
  }

  return "The coupon code you entered doesn't exist. Please try again.";
};

const PromoCodeSubmission: React.FC<PromoCodeSubmissionProps> = ({
  promoMessage,
  promotion,
  showLoadingState,
  isLoading,
  removePromoCodeHandler,
  handlePromoCodeSubmit,
  control,
  applyPromoMutation,
  formState,
  advertisedPromosElement,
  wasAdvertisedPromoSubmitted,
  promoCodeInputValue,
  setPromoCodeInputValue,
  shouldShowInputIcon,
  isVariant = false,
}) => {
  const isFormDirtyAndMutationError =
    formState.isDirty && applyPromoMutation.isError;
  const isFormDirtyAndHasErrorMessage =
    formState.isDirty && Boolean(formState.errors.promoCode?.message);
  const isMutationErrorAndAdvertisedPromoSubmitted =
    applyPromoMutation.isError && wasAdvertisedPromoSubmitted;

  const isFormInvalid =
    isFormDirtyAndMutationError ||
    isFormDirtyAndHasErrorMessage ||
    isMutationErrorAndAdvertisedPromoSubmitted;

  const handleCodeRemoval = (event: React.MouseEvent<HTMLButtonElement>) => {
    removePromoCodeHandler(event);
    setPromoCodeInputValue('');
  };

  const trackFreePromoCodeSubmission = (promoCode: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Free promo code submitted',
        promoCode,
      },
    });
  };

  return (
    <>
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {promoMessage}
      </VisuallyHidden>
      {promotion ? (
        <Box
          display="flex"
          width="100%"
          gap={2}
          background="tints.white"
          padding={4}
          borderRadius={12}
        >
          <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
            <Box display="flex" flexDirection="column" gap={1} flexGrow="1">
              <Text fontSize="md" fontWeight="medium">
                {promotion?.toUpperCase()}{' '}
                <Text as="span" fontSize="md" color="tints.darkGrey">
                  applied
                </Text>
              </Text>
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <IconButton
              icon={<Trash />}
              variant="primary"
              colorScheme="whiteCS"
              color="utility.sale"
              aria-label="Remove promo code"
              size="sm"
              padding={4}
              marginY={-4}
              isLoading={showLoadingState && isLoading}
              _loading={{
                backgroundColor: 'tints.white',
              }}
              sx={{
                '& svg': {
                  fontSize: '1.25rem !important',
                },
              }}
              onClick={handleCodeRemoval}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box
            as="form"
            display="flex"
            noValidate
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            onSubmit={handlePromoCodeSubmit}
          >
            <FormControl isInvalid={isFormInvalid}>
              <Controller
                control={control}
                name="promoCode"
                render={(field) => (
                  <InputGroup>
                    {shouldShowInputIcon ? (
                      <InputLeftElement pointerEvents="none">
                        <Icon as={Tag} color="tints.darkGrey" />
                      </InputLeftElement>
                    ) : null}
                    <Input
                      {...field}
                      onChange={(event) => {
                        applyPromoMutation.reset();
                        field.onChange(event);
                        setPromoCodeInputValue(event.target.value);
                      }}
                      placeholder="Add promo code"
                      name="promoCode"
                      aria-label="promoCode"
                      {...(isVariant && { borderColor: 'tints.lightGrey' })}
                      readOnly={showLoadingState && isLoading}
                      value={promoCodeInputValue || ''}
                    />
                  </InputGroup>
                )}
              />
              <FormErrorMessage
                sx={{
                  a: {
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                    '&:focus': {
                      textDecoration: 'underline',
                    },
                  },
                }}
              >
                {getPromoErrorMessage(formState, applyPromoMutation)}
              </FormErrorMessage>
            </FormControl>
            <Flex alignItems="center">
              <IconButton
                icon={<Plus />}
                type="submit"
                variant="primary"
                colorScheme="whiteCS"
                color="tints.darkGrey"
                padding="2"
                isLoading={showLoadingState && isLoading}
                aria-label="Apply promo code"
                size="sm"
                marginTop="5px"
                onClick={() =>
                  trackFreePromoCodeSubmission(promoCodeInputValue)
                }
              />
            </Flex>
          </Box>

          {advertisedPromosElement}
        </>
      )}
    </>
  );
};
export default PromoCodeSubmission;
